import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { MapPin, Envelope, ArrowLeft, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useFingerprint } from "@shared/hooks/useFingerprint";
import { getCurrentLanguage } from "@shared/lib/i18n";
import type { Id } from "@convex/_generated/dataModel";

export const Route = createFileRoute("/auth/login")({
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const currentLang = getCurrentLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  // Folosim FingerprintJS pentru device fingerprinting
  const { fingerprint: deviceFingerprint } = useFingerprint();

  // Verifică dacă există userId salvat (auto-login)
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      // Verifică dacă utilizatorul este încă valid
      // Redirect direct la app dacă există sesiune validă
      navigate({ to: "/app" });
      return;
    }

    // Încarcă email-ul salvat dacă există
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, [navigate]);

  const isSuperadmin = email.toLowerCase() === "superadmin@suuper.ro";

  const loginUser = useMutation(api.auth.loginUser);
  const sendOTP = useMutation(api.auth.sendOTP);

  // Funcție helper pentru formatarea timpului
  const formatRetryTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes === 0) {
      if (seconds === 1) {
        return t("rateLimit.oneSecond", { defaultValue: "1 secundă" });
      }
      return t("rateLimit.seconds", { count: seconds, defaultValue: `${seconds} secunde` });
    }

    if (remainingSeconds === 0) {
      if (minutes === 1) {
        return t("rateLimit.oneMinute", { defaultValue: "1 minut" });
      }
      return t("rateLimit.minutes", { count: minutes, defaultValue: `${minutes} minute` });
    }

    // Atât minute cât și secunde
    const minutesText = minutes === 1
      ? t("rateLimit.oneMinute", { defaultValue: "1 minut" })
      : t("rateLimit.minutes", { count: minutes, defaultValue: `${minutes} minute` });
    
    const secondsText = remainingSeconds === 1
      ? t("rateLimit.oneSecond", { defaultValue: "1 secundă" })
      : t("rateLimit.seconds", { count: remainingSeconds, defaultValue: `${remainingSeconds} secunde` });

    return currentLang === "ro" 
      ? `${minutesText} și ${secondsText}`
      : `${minutesText} and ${secondsText}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Așteaptă fingerprint-ul dacă nu e gata
    if (!deviceFingerprint) {
      console.error("[Login] Device fingerprint not ready");
      setError("generic");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setRetryAfter(null);

    try {
      console.log("[Login] Attempting login for:", email.toLowerCase());

      // Login utilizator
      const result = await loginUser({
        email: email.toLowerCase(),
        deviceFingerprint,
        userAgent: navigator.userAgent,
        password: isSuperadmin ? password : undefined,
      });

      console.log("[Login] loginUser result:", result);

      if (!result.success) {
        console.error("[Login] Login failed:", result.error);
        setError(result.error || "generic");
        setIsLoading(false);
        return;
      }

      // Dacă e superadmin și parola e corectă, loghează direct
      if (isSuperadmin && result.requiresPassword === false) {
        console.log("[Login] Superadmin login successful");
        try {
          localStorage.setItem("userId", result.userId!);
        } catch (err) {
          console.error("[Login] Failed to save userId to localStorage:", err);
        }
        
        // Identificare PostHog (nu blochează flow-ul dacă eșuează)
        try {
          const { identifyUser, trackEvent } = await import("@shared/lib/posthog-lazy");
          await identifyUser(result.userId! as Id<"users">, {
            email: email.toLowerCase(),
            role: "superadmin",
          });
          await trackEvent("user_logged_in", {
            email: email.toLowerCase(),
            isSuperadmin: true,
          });
        } catch (err) {
          console.error("[Login] PostHog tracking failed:", err);
        }
        
        window.location.href = "/admin";
        return;
      }

      // Pentru superadmin, dacă nu a trecut verificarea parolei, cere parola
      if (isSuperadmin && result.requiresPassword === true) {
        console.error("[Login] Superadmin password required/invalid");
        setError("invalidPassword");
        setIsLoading(false);
        return;
      }

      // Pentru utilizatori normali, trimite OTP pentru verificare
      if (result.phone) {
        console.log("[Login] Sending OTP to:", result.phone);
        const otpResult = await sendOTP({
          phone: result.phone,
          type: "verify",
          deviceFingerprint: deviceFingerprint,
        });

        console.log("[Login] sendOTP result:", otpResult);

        if (!otpResult.success) {
          console.error("[Login] OTP send failed:", otpResult.error, "retryAfter:", otpResult.retryAfter);
          if (otpResult.retryAfter) {
            setError("rateLimit.otpTooManyRequests");
            setRetryAfter(otpResult.retryAfter);
          } else {
            setError(otpResult.error || "generic");
          }
          setIsLoading(false);
          return;
        }
      } else {
        console.error("[Login] No phone number found for user");
        setError("generic");
        setIsLoading(false);
        return;
      }

      // Salvează email-ul dacă "Ține-mă minte" este bifat
      try {
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email.toLowerCase());
        } else {
          localStorage.removeItem("rememberedEmail");
        }
      } catch (err) {
        console.error("[Login] Failed to save rememberedEmail:", err);
      }

      // Salvează datele în sessionStorage pentru pagina de verificare
      try {
        sessionStorage.setItem("pendingVerification", JSON.stringify({
          userId: result.userId,
          phone: result.phone,
          email: email,
          rememberMe: rememberMe,
          isLogin: true, // Flag pentru a distinge login de register
        }));
      } catch (err) {
        console.error("[Login] Failed to save pendingVerification:", err);
        setError("generic");
        setIsLoading(false);
        return;
      }

      // Redirecționează la verificare
      navigate({ to: "/auth/verify" });
    } catch (err) {
      console.error("[Login] Unexpected error:", err);
      setError("generic");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-text-light hover:text-text transition-colors"
        >
          <ArrowLeft size={20} />
          {t("common:actions.back")}
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
              <MapPin size={9} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-text">{t("login.title")}</h1>
            <p className="text-text-light mt-1">{t("login.subtitle")}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text mb-2"
              >
                {t("login.email")}
              </label>
              <div className="relative">
                <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  placeholder={t("login.emailPlaceholder")}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            </div>

            {isSuperadmin && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-text mb-2"
                >
                  {t("login.password")}
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    placeholder="••••••••"
                    required
                    className="w-full pl-12 pr-12 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                  >
                    {showPassword ? (
                      <EyeSlash size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-xl">
                <p className="text-error text-sm">
                  {error.startsWith("rateLimit.")
                    ? t(`auth:${error}`, { retryAfter: retryAfter ? formatRetryTime(retryAfter) : "0 secunde" })
                    : t(`errors.${error}`, { 
                        defaultValue: t("errors.generic", { defaultValue: "A apărut o eroare. Încearcă din nou." })
                      })}
                </p>
              </div>
            )}

            {/* Remember me checkbox */}
            <div className="flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                size={16} className="text-primary bg-white border-border rounded focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer"
              />
              <label
                htmlFor="rememberMe"
                className="ml-2 text-sm text-text cursor-pointer select-none"
              >
                {t("login.rememberMe")}
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t("common:actions.loading") : t("login.submit")}
            </button>
          </form>

          {/* Register link */}
          <div className="mt-8 text-center">
            <p className="text-text-light">
              {t("login.noAccount")}{" "}
              <Link
                to="/auth/register"
                className="text-primary font-medium hover:underline"
              >
                {t("login.register")}
              </Link>
            </p>
          </div>

          {/* Partner/Admin links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-center gap-6 text-sm">
              <a
                href="/partner/login"
                className="text-text-light hover:text-primary transition-colors"
              >
                {t("partner.title")}
              </a>
              <span className="text-border">|</span>
              <a
                href="/admin"
                className="text-text-light hover:text-primary transition-colors"
              >
                {t("admin.title")}
              </a>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
